import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface Scan {
  uuid: string;
  product: string;
  status: string;
  [key: string]: any;
}

interface EncephalonContextType {
  studyExists: boolean;
  setStudyExists: (exists: boolean) => void;
  scans: Scan[];
  setScans: (scans: Scan[]) => void;
  scanExists: (productType: string) => boolean;
  currentStudyUuid: string | null;
  setCurrentStudyUuid: (uuid: string | null) => void;
  anyScanExists: boolean;
  triggerScanUpdate: () => void;
  clearEncephalonState: () => void;
}

const EncephalonContext = createContext<EncephalonContextType | undefined>(undefined);

export const EncephalonProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [studyExists, setStudyExists] = useState(false);
  const [scans, setScans] = useState<Scan[]>([]);
  const [currentStudyUuid, setCurrentStudyUuid] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [updateCount, setUpdateCount] = useState<number>(0);

  const scanExists = useCallback(
    (productType: string): boolean =>
      scans.some(s => s.product?.toLowerCase() === productType?.toLowerCase()),
    [scans]
  );

  const anyScanExists = scans.length > 0;

  const triggerScanUpdate = useCallback(() => {
    setUpdateCount((prev: number) => prev + 1);
  }, []);

  const clearEncephalonState = useCallback(() => {
    setStudyExists(false);
    setScans([]);
    setCurrentStudyUuid(null);
  }, []);

  // Clear scans when study is removed
  useEffect(() => {
    if (!studyExists) {
      setScans([]);
    }
  }, [studyExists]);

  return (
    <EncephalonContext.Provider
      value={{
        studyExists,
        setStudyExists,
        scans,
        setScans,
        scanExists,
        currentStudyUuid,
        setCurrentStudyUuid,
        anyScanExists,
        triggerScanUpdate,
        clearEncephalonState,
      }}
    >
      {children}
    </EncephalonContext.Provider>
  );
};

export const useEncephalon = (): EncephalonContextType => {
  const context = useContext(EncephalonContext);
  if (context === undefined) {
    throw new Error('useEncephalon must be used within an EncephalonProvider');
  }
  return context;
};
